import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, CardContent, Button, Typography, CircularProgress, InputAdornment, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Alert,  } from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Sms as SmsIcon,
  Close as CloseIcon,
  PhoneAndroid as MobileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'react-toastify';
import { loggedInState, userInfoState } from '../recoil/atoms';
import client from '../api/client';

// عکس بک‌گراند
import bgImage from '../assets/images/login-bg.jpg';
import CustomTextField from '../components/CustomTextField';

const Login = () => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP States
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpStep, setOtpStep] = useState('mobile');
  const [otpMobile, setOtpMobile] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [countdown, setCountdown] = useState(0);

  const otpInputRefs = useRef([]);

  const navigate = useNavigate();
  const setLoggedIn = useSetRecoilState(loggedInState);
  const setUserInfo = useSetRecoilState(userInfoState);

  const toEnglishDigits = (str) => {
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    const arabicDigits = '٠١٢٣٤٥٦٧٨٩';
    let result = String(str);
    for (let i = 0; i < 10; i++) {
      result = result.replace(new RegExp(persianDigits[i], 'g'), i);
      result = result.replace(new RegExp(arabicDigits[i], 'g'), i);
    }
    return result;
  };

  // تایمر شمارش معکوس
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // ریست OTP
  useEffect(() => {
    if (!otpDialogOpen) {
      setOtpStep('mobile');
      setOtpMobile('');
      setOtpCode(['', '', '', '', '']);
      setOtpError('');
      setCountdown(0);
    }
  }, [otpDialogOpen]);

  // فوکوس اولیه
  useEffect(() => {
    if (otpStep === 'verify' && otpInputRefs.current[0]) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [otpStep]);

  // هندلر موفقیت لاگین
  const handleLoginSuccess = (tokens, user) => {
    localStorage.setItem('access', tokens.access);
    localStorage.setItem('refresh', tokens.refresh);
    localStorage.setItem('user', JSON.stringify(user));

    setLoggedIn(true);
    setUserInfo(user);

    toast.success('خوش آمدید!');

    if (user.first_time_login) {
      navigate('/change-password');
    } else if (user.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/shop');
    }
  };

  // ورود با رمز عبور
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mobile || !password) {
      toast.error('لطفاً موبایل و رمز عبور را وارد کنید');
      return;
    }

    setLoading(true);
    try {
      const response = await client.post('/users/login/', {
        username: toEnglishDigits(mobile),
        password: password,
      });

      const { tokens, user } = response.data;
      handleLoginSuccess(tokens, user);
    } catch (error) {
      const message = error.response?.data?.detail || 
                      error.response?.data?.error || 
                      'خطا در ورود';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // درخواست OTP
  const handleRequestOTP = async () => {
    setOtpError('');
    setOtpLoading(true);

    try {
      const response = await client.post('/users/otp/request/', {
        mobile: toEnglishDigits(otpMobile),
      });

      setOtpStep('verify');
      setCountdown(response.data.expire_seconds || 120);
      setOtpCode(['', '', '', '', '']);
    } catch (err) {
      setOtpError(
        err.response?.data?.error ||
        'خطا در ارسال کد تایید'
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // تایید OTP
  const handleVerifyOTP = async () => {
    const code = otpCode.join('');
    
    if (code.length !== 5) {
      setOtpError('لطفاً کد 5 رقمی را کامل وارد کنید');
      return;
    }

    setOtpError('');
    setOtpLoading(true);

    try {
      const response = await client.post('/users/otp/verify/', {
        mobile: toEnglishDigits(otpMobile),
        code: code,
      });

      const { tokens, user } = response.data;
      setOtpDialogOpen(false);
      handleLoginSuccess(tokens, user);
    } catch (err) {
      setOtpError(
        err.response?.data?.error ||
        'کد وارد شده اشتباه است'
      );
      setOtpCode(['', '', '', '', '']);
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } finally {
      setOtpLoading(false);
    }
  };

  // ارسال مجدد OTP
  const handleResendOTP = async () => {
    setOtpError('');
    setOtpLoading(true);

    try {
      const response = await client.post('/users/otp/resend/', {
        mobile: toEnglishDigits(otpMobile),
      });

      setCountdown(response.data.expire_seconds || 120);
      setOtpCode(['', '', '', '', '']);
      toast.success('کد جدید ارسال شد');
    } catch (err) {
      if (err.response?.data?.wait_seconds) {
        setCountdown(err.response.data.wait_seconds);
      }
      setOtpError(err.response?.data?.error || 'خطا در ارسال مجدد');
    } finally {
      setOtpLoading(false);
    }
  };

  // هندل تغییر اینپوت OTP - فقط یک رقم
  const handleOtpInputChange = (index, e) => {
    const value = e.target.value;
    const lastChar = value.slice(-1);
    const digit = toEnglishDigits(lastChar);
    
    // فقط عدد قبول کن
    if (digit && !/^\d$/.test(digit)) return;

    const newOtp = [...otpCode];
    newOtp[index] = digit;
    setOtpCode(newOtp);

    // برو به خانه بعدی
    if (digit && index < 4) {
      setTimeout(() => {
        otpInputRefs.current[index + 1]?.focus();
      }, 10);
    }
  };

  // هندل کلید فشرده شده
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otpCode];
      
      if (otpCode[index]) {
        // اگه خونه پره، پاکش کن
        newOtp[index] = '';
        setOtpCode(newOtp);
      } else if (index > 0) {
        // اگه خالیه، برو عقب و اون رو پاک کن
        newOtp[index - 1] = '';
        setOtpCode(newOtp);
        otpInputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 4) {
      otpInputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      handleVerifyOTP();
    }
  };

  // هندل Paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = toEnglishDigits(e.clipboardData.getData('text'))
      .replace(/\D/g, '')
      .slice(0, 5);
    
    if (pastedData.length > 0) {
      const newOtp = pastedData.split('').concat(Array(5).fill('')).slice(0, 5);
      setOtpCode(newOtp);
      
      // فوکوس روی آخرین خانه پر شده یا خانه بعدی
      const focusIndex = Math.min(pastedData.length, 4);
      setTimeout(() => {
        otpInputRefs.current[focusIndex]?.focus();
      }, 10);
    }
  };

  // فرمت زمان
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // چک کد کامل
  const isOtpComplete = otpCode.every(d => d !== '');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1,
        },
      }}
    >
      <Card 
        sx={{ 
          width: '100%', 
          maxWidth: 400, 
          position: 'relative', 
          zIndex: 2,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#1a237e',
                textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              گالری پرده 
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              سامانه مدیریت گالری پرده 
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <CustomTextField               label="شماره موبایل"
              value={mobile}
              onChange={(e) => setMobile(toEnglishDigits(e.target.value))}
              placeholder="09123456789"
              inputProps={{ maxLength: 11, dir: 'ltr' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
              fullWidth
            />

            <CustomTextField               label="رمز عبور"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ 
                py: 1.5,
                background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
                boxShadow: '0 3px 5px 2px rgba(26, 35, 126, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0d1642 30%, #1a237e 90%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'ورود به سیستم'}
            </Button>
          </form>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              یا
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<SmsIcon />}
            onClick={() => setOtpDialogOpen(true)}
            sx={{ 
              py: 1.5,
              borderColor: '#1a237e',
              color: '#1a237e',
              '&:hover': {
                borderColor: '#0d1642',
                backgroundColor: 'rgba(26, 35, 126, 0.04)',
              },
            }}
          >
            ورود با رمز یکبار مصرف
          </Button>

          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            © محمد نشیبی -1404
          </Typography>
        </CardContent>
      </Card>

      {/* دیالوگ OTP */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => !otpLoading && setOtpDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <IconButton
            onClick={() => setOtpDialogOpen(false)}
            disabled={otpLoading}
            sx={{ position: 'absolute', left: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MobileIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>
          
          <Typography variant="h6" sx={{ color: '#1a237e' }}>
            {otpStep === 'mobile' ? 'ورود با رمز یکبار مصرف' : 'تایید شماره موبایل'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {otpStep === 'mobile'
              ? 'شماره موبایل خود را وارد کنید'
              : `کد ارسال شده به ${otpMobile} را وارد کنید`}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {otpError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {otpError}
            </Alert>
          )}

          {otpStep === 'mobile' && (
            <CustomTextField               fullWidth
              label="شماره موبایل"
              value={otpMobile}
              onChange={(e) => setOtpMobile(toEnglishDigits(e.target.value))}
              inputProps={{
                maxLength: 11,
                style: { textAlign: 'center', direction: 'ltr', fontSize: 18 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="09123456789"
              disabled={otpLoading}
            />
          )}

          {otpStep === 'verify' && (
            <>
              {/* اینپوت‌های OTP */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  direction: 'ltr',
                  mb: 2,
                }}
              >
                {otpCode.map((digit, index) => (
                  <CustomTextField                     key={index}
                    inputRef={(el) => (otpInputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleOtpInputChange(index, e)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    onFocus={(e) => e.target.select()}
                    inputProps={{
                      maxLength: 2,
                      style: {
                        textAlign: 'center',
                        fontSize: 24,
                        fontWeight: 'bold',
                        padding: '12px 0',
                        caretColor: 'transparent',
                      },
                    }}
                    sx={{ 
                      width: 50,
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#1a237e',
                          borderWidth: 2,
                        },
                      },
                    }}
                    disabled={otpLoading}
                  />
                ))}
              </Box>

              {/* دکمه تایید */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerifyOTP}
                disabled={otpLoading || !isOtpComplete}
                sx={{ 
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #0d1642 30%, #1a237e 90%)',
                  },
                  '&:disabled': {
                    background: '#ccc',
                  },
                }}
              >
                {otpLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'تایید و ورود'
                )}
              </Button>

              {/* تایمر */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {countdown > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    اعتبار کد:{' '}
                    <Typography
                      component="span"
                      sx={{ fontFamily: 'monospace', color: '#1a237e', fontWeight: 'bold' }}
                    >
                      {formatTime(countdown)}
                    </Typography>
                  </Typography>
                ) : (
                  <Button
                    variant="text"
                    onClick={handleResendOTP}
                    disabled={otpLoading}
                    sx={{ color: '#1a237e' }}
                  >
                    ارسال مجدد کد
                  </Button>
                )}
              </Box>

              <Button
                fullWidth
                variant="text"
                onClick={() => setOtpStep('mobile')}
                disabled={otpLoading}
                sx={{ color: 'text.secondary' }}
              >
                ← تغییر شماره موبایل
              </Button>
            </>
          )}
        </DialogContent>

        {otpStep === 'mobile' && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleRequestOTP}
              disabled={otpLoading || otpMobile.length < 10}
              sx={{ 
                py: 1.5,
                background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0d1642 30%, #1a237e 90%)',
                },
              }}
            >
              {otpLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'دریافت کد تایید'
              )}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default Login;